/**
 * クライアント側 canvas で画像をリサイズ + JPEG 圧縮するヘルパ。
 * LINE Messaging API（1024×1024 / 10MB）等のサイズ制約に画像を合わせるために使用。
 */

export interface ResizeResult {
  blob: Blob;
  ext: string;
  width: number;
  height: number;
}

interface ResizeOptions {
  /** 最大サイズ（長辺基準）。指定なしならリサイズなし */
  maxDimension?: number;
  /** 最大バイト数。超過時は品質を段階的に下げて再圧縮 */
  maxBytes?: number;
  /** JPEG 品質候補（高い順） */
  qualities?: number[];
}

const DEFAULT_QUALITIES = [0.92, 0.85, 0.75, 0.6, 0.5];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('画像を読み込めませんでした'));
    };
    img.src = url;
  });
}

/** 画像を最大長辺サイズに収まるよう canvas でリサイズし、JPEG に圧縮して返す。 */
export async function resizeImage(
  file: File,
  options: ResizeOptions = {},
): Promise<ResizeResult> {
  const { maxDimension, maxBytes, qualities = DEFAULT_QUALITIES } = options;
  const img = await loadImage(file);
  let { width, height } = img;

  if (maxDimension && (width > maxDimension || height > maxDimension)) {
    if (width >= height) {
      height = Math.round((height / width) * maxDimension);
      width = maxDimension;
    } else {
      width = Math.round((width / height) * maxDimension);
      height = maxDimension;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas context を取得できません');
  ctx.drawImage(img, 0, 0, width, height);

  for (const q of qualities) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', q),
    );
    if (!blob) continue;
    if (!maxBytes || blob.size <= maxBytes) {
      return { blob, ext: 'jpg', width, height };
    }
  }
  throw new Error('画像が大きすぎます（最終圧縮でも上限超過）');
}
